import { useEffect, useState } from "react";

import { fetchComparableProfile, type ComparableArtist, type ComparableProfile } from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatMonth } from "./discoverCopy";

type Props = {
  artistId: string;
  comparable: ComparableArtist | null;
  onClose: () => void;
  onViewVenues: (comparable: ComparableArtist) => void;
};

const n = (value: number | null | undefined) => (value == null ? null : value.toLocaleString());

/**
 * Comparable artist detail (owner 2026-10-24): genres, followers, links and touring footprint from stored shared
 * evidence. Read-only; never calls an external provider. Similar artists are intentionally not shown.
 */
export default function ComparableArtistSheet({ artistId, comparable, onClose, onViewVenues }: Props) {
  const publicId = comparable?.public_artist.id ?? null;
  const [profile, setProfile] = useState<ComparableProfile | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setProfile(null);
    setFailed(false);
    if (!publicId) return;
    let cancelled = false;
    fetchComparableProfile(publicId, artistId)
      .then((payload) => !cancelled && setProfile(payload))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [publicId, artistId]);

  const fp = profile?.footprint;
  const size = fp?.venue_size;
  const followers = [
    n(profile?.followers.spotify) && `${n(profile?.followers.spotify)} on Spotify`,
    n(profile?.followers.bandsintown) && `${n(profile?.followers.bandsintown)} on Bandsintown`,
  ].filter(Boolean);

  return (
    <Sheet open={Boolean(comparable)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="max-h-[90dvh] w-full overflow-y-auto sm:max-w-lg">
        {comparable && (
          <>
            <SheetHeader>
              <SheetTitle>{comparable.public_artist.name}</SheetTitle>
              <SheetDescription>{profile?.genres.length ? profile.genres.join(" · ") : "Comparable artist"}</SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-5 text-sm">
              {!profile && !failed && <p>Loading artist details…</p>}
              {failed && <p>Artist details couldn't be loaded right now.</p>}

              {profile && fp && (
                <>
                  <section aria-labelledby="artist-audience">
                    <h3 id="artist-audience" className="mb-1 font-semibold text-foreground">Audience</h3>
                    <p className="text-muted-foreground">{followers.length ? followers.join(" · ") : "No follower counts yet."}</p>
                    <div className="mt-1 flex flex-wrap gap-3">
                      {profile.links.website && <a className="text-xs text-primary hover:underline" href={profile.links.website} target="_blank" rel="noreferrer">Website ↗</a>}
                      {profile.links.spotify && <a className="text-xs text-primary hover:underline" href={profile.links.spotify} target="_blank" rel="noreferrer">Spotify ↗</a>}
                      {profile.links.bandsintown && <a className="text-xs text-primary hover:underline" href={profile.links.bandsintown} target="_blank" rel="noreferrer">Bandsintown ↗</a>}
                    </div>
                  </section>

                  <section aria-labelledby="artist-footprint">
                    <h3 id="artist-footprint" className="mb-1 font-semibold text-foreground">Touring footprint</h3>
                    {fp.shows === 0 ? (
                      <p className="text-muted-foreground">No shows collected yet.</p>
                    ) : (
                      <>
                        <p className="text-foreground/90">
                          {fp.shows.toLocaleString()} shows at {fp.venues.toLocaleString()} venues
                          {fp.first_show && fp.last_show ? ` · ${formatMonth(fp.first_show)} – ${formatMonth(fp.last_show)}` : ""}
                          {fp.upcoming ? ` · ${fp.upcoming} upcoming` : ""}
                        </p>
                        {fp.states.length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Shows by state">
                            {fp.states.map((s) => (
                              <li key={s.state} className="rounded bg-muted px-2 py-0.5 text-xs">
                                {s.state} <span className="text-muted-foreground">{s.shows}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </section>

                  {fp.shows > 0 && (
                    <section aria-labelledby="artist-rooms">
                      <h3 id="artist-rooms" className="mb-1 font-semibold text-foreground">Typical rooms</h3>
                      {size?.median != null ? (
                        <p className="text-foreground/90">
                          About {size.median.toLocaleString()} capacity (range {size.min?.toLocaleString()}–{size.max?.toLocaleString()}, from {size.known_venues} venues with a known capacity)
                        </p>
                      ) : (
                        <p className="text-muted-foreground">Venue capacity isn't known for enough of these venues yet.</p>
                      )}
                      {fp.venue_types.length > 0 && (
                        <ul className="mt-1 text-muted-foreground" aria-label="Venue types played">
                          {fp.venue_types.slice(0, 5).map((t) => (
                            <li key={t.type}>{t.type} · {t.venues}</li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}

                  {fp.shows > 0 && (
                    <Button size="sm" onClick={() => onViewVenues(comparable)}>View venues</Button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
